import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/use-auth'
import { useComprehensiveScorecard } from '@/hooks/use-comprehensive-scorecard'
import { useTimeframeScorecards } from '@/hooks/use-timeframe-scorecards'
import { useBusinessAreas } from '@/hooks/use-business-areas'
import { ComprehensiveScorecardTable } from '@/components/comprehensive-scorecard-table'
import type { ColumnCount, LabelType, ComprehensiveMetricRow } from '@/types'
import { TIMEFRAME_LABELS, type Timeframe } from '@/types'

type GroupBy = 'none' | 'owner' | 'timeframe' | 'area'

// Detect mobile for default column count
const isMobile = typeof window !== 'undefined' && window.innerWidth < 640

export function Dashboard() {
  const { user } = useAuth()

  // NewUser role sees empty state; no data hooks run for them
  if (user?.role === 'new_user') {
    return (
      <div className="text-center py-16">
        <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">No Metrics Assigned</h2>
        <p className="text-gray-600 max-w-md mx-auto">
          You haven't been assigned any metrics yet. Contact your administrator to get started.
        </p>
      </div>
    )
  }

  return <DashboardContent />
}

function DashboardContent() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [columns, setColumns] = useState<ColumnCount>(isMobile ? 4 : 8)
  const [labelType, setLabelType] = useState<LabelType>('date_range')

  // Use regular scorecard for date_range mode
  const regularScorecard = useComprehensiveScorecard(columns, labelType, undefined)
  // Use timeframe-specific scorecards for period mode
  const timeframeScorecards = useTimeframeScorecards(columns, labelType === 'period')

  // Select the appropriate data source
  const isTimeframeModeActive = labelType === 'period'
  const data = isTimeframeModeActive ? null : regularScorecard.data
  const isLoading = isTimeframeModeActive ? timeframeScorecards.isLoading : regularScorecard.isLoading
  const error = isTimeframeModeActive ? timeframeScorecards.error : regularScorecard.error

  const { data: areas } = useBusinessAreas()

  // Filter state
  const [ownerFilter, setOwnerFilter] = useState<string>('')
  const [areaFilter, setAreaFilter] = useState<string>('')

  // Grouping state - single dropdown
  const [groupBy, setGroupBy] = useState<GroupBy>('owner')

  // Controls panel collapsed by default
  const [controlsOpen, setControlsOpen] = useState(false)

  const handleRowClick = (metric: ComprehensiveMetricRow) => {
    navigate(`/metrics/${metric.metric_id}`)
  }

  // Extract unique owners for filter dropdown
  const uniqueOwners = useMemo(() => {
    if (!data) return []
    const owners = new Map<string, string>()
    data.metrics.forEach(m => {
      if (m.owner_id && m.owner_name) {
        owners.set(m.owner_id, m.owner_name)
      }
    })
    return Array.from(owners.entries()).map(([id, name]) => ({ id, name }))
  }, [data])

  // Filter metrics
  const filteredMetrics = useMemo(() => {
    if (!data) return []
    return data.metrics.filter(m => {
      if (ownerFilter && m.owner_id !== ownerFilter) return false
      if (areaFilter && m.area_id !== areaFilter) return false
      return true
    })
  }, [data, ownerFilter, areaFilter])

  // Split into My Metrics and Team Metrics
  const myMetrics = useMemo(() => {
    return filteredMetrics.filter(m => m.owner_id === user?.id)
  }, [filteredMetrics, user?.id])

  const teamMetrics = useMemo(() => {
    return filteredMetrics.filter(m => m.owner_id !== user?.id)
  }, [filteredMetrics, user?.id])

  // Generic grouping function
  const groupMetrics = (metrics: ComprehensiveMetricRow[], by: GroupBy) => {
    if (by === 'none') return null

    const groups = new Map<string, { label: string; metrics: ComprehensiveMetricRow[] }>()

    metrics.forEach(m => {
      let key: string
      let label: string

      switch (by) {
        case 'owner':
          key = m.owner_id || 'unassigned'
          label = m.owner_name || 'Unassigned'
          break
        case 'timeframe':
          key = m.timeframe || 'none'
          label = TIMEFRAME_LABELS[m.timeframe as Timeframe] || 'No Timeframe'
          break
        case 'area':
          key = m.area_id || 'none'
          label = m.area_name || 'No Area'
          break
        default:
          return
      }

      if (!groups.has(key)) {
        groups.set(key, { label, metrics: [] })
      }
      groups.get(key)!.metrics.push(m)
    })

    // Sort groups
    const sortedEntries = Array.from(groups.entries())
    if (by === 'timeframe') {
      const order = ['weekly', 'monthly', 'quarterly', 'biannual', 'yearly', 'none']
      sortedEntries.sort((a, b) => {
        const aIdx = order.indexOf(a[0])
        const bIdx = order.indexOf(b[0])
        return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx)
      })
    } else {
      sortedEntries.sort((a, b) => a[1].label.localeCompare(b[1].label))
    }

    return sortedEntries
  }

  // Personalized stats (user's goals only) for Monthly view
  const mySummary = useMemo(() => {
    if (!myMetrics.length) return { met: 0, missed: 0, pending: 0 }
    return {
      met: myMetrics.reduce((acc, m) => acc + m.values.filter(v => v.status === 'met').length, 0),
      missed: myMetrics.reduce((acc, m) => acc + m.values.filter(v => v.status === 'missed').length, 0),
      pending: myMetrics.reduce((acc, m) => acc + m.values.filter(v => v.status === 'pending').length, 0),
    }
  }, [myMetrics])

  // Team total (ALL goals met) for Monthly view
  const teamMetCount = useMemo(() => {
    return filteredMetrics.reduce((acc, m) => acc + m.values.filter(v => v.status === 'met').length, 0)
  }, [filteredMetrics])

  // Personalized stats for Time Period mode
  const timeframeSummary = useMemo(() => {
    if (!isTimeframeModeActive) return { myMet: 0, myMissed: 0, myPending: 0, teamMet: 0 }
    let myMet = 0, myMissed = 0, myPending = 0, teamMet = 0

    timeframeScorecards.dataByTimeframe.forEach((tfData) => {
      // Apply filters
      const filtered = tfData.metrics.filter(m => {
        if (ownerFilter && m.owner_id !== ownerFilter) return false
        if (areaFilter && m.area_id !== areaFilter) return false
        return true
      })
      const myTfMetrics = filtered.filter(m => m.owner_id === user?.id)

      myMet += myTfMetrics.reduce((acc, m) => acc + m.values.filter(v => v.status === 'met').length, 0)
      myMissed += myTfMetrics.reduce((acc, m) => acc + m.values.filter(v => v.status === 'missed').length, 0)
      myPending += myTfMetrics.reduce((acc, m) => acc + m.values.filter(v => v.status === 'pending').length, 0)
      teamMet += filtered.reduce((acc, m) => acc + m.values.filter(v => v.status === 'met').length, 0)
    })

    return { myMet, myMissed, myPending, teamMet }
  }, [isTimeframeModeActive, timeframeScorecards.dataByTimeframe, user?.id, ownerFilter, areaFilter])

  // In period mode, force grouping by timeframe
  const effectiveGroupBy = labelType === 'period' ? 'timeframe' : groupBy

  // Render grouped metrics
  const renderGroupedMetrics = (metrics: ComprehensiveMetricRow[]) => {
    const grouped = groupMetrics(metrics, effectiveGroupBy)

    if (!grouped) {
      return (
        <ComprehensiveScorecardTable
          metrics={metrics}
          periods={data!.periods}
          onRowClick={handleRowClick}
        />
      )
    }

    return grouped.map(([key, { label, metrics: groupMetrics }]) => (
      <div key={key} className="space-y-2">
        <h3 className="text-sm font-medium text-gray-700 pl-2 flex items-center gap-2">
          {effectiveGroupBy === 'owner' && (
            <span className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-600">
              {label.charAt(0)}
            </span>
          )}
          {label}
          <span className="text-xs text-gray-400">({groupMetrics.length})</span>
        </h3>
        <ComprehensiveScorecardTable
          metrics={groupMetrics}
          periods={data!.periods}
          onRowClick={handleRowClick}
          timeframe={labelType === 'period' ? (key as Timeframe) : undefined}
        />
      </div>
    ))
  }

  // Check if any filter/grouping is non-default
  const hasActiveFilters = ownerFilter || areaFilter || groupBy !== 'owner'

  return (
    <div className="space-y-4">
      {/* Page header with collapsible controls */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Team Scorecard</h1>
        <button
          onClick={() => setControlsOpen(!controlsOpen)}
          className="flex items-center gap-1.5 px-2 py-1 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
        >
          <svg className={`w-4 h-4 transition-transform ${controlsOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          Options
          {hasActiveFilters && <span className="w-1.5 h-1.5 rounded-full bg-brand-teal" />}
        </button>
      </div>

      {/* Collapsible controls panel */}
      {controlsOpen && (
        <div className="bg-white p-3 rounded-lg border border-gray-200 space-y-3">
          {/* Row 1: Display options */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-gray-500 uppercase">Display:</span>
            <select
              value={columns}
              onChange={(e) => setColumns(Number(e.target.value) as ColumnCount)}
              className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
            >
              <option value={4}>{isTimeframeModeActive ? '4 Periods' : '4 months'}</option>
              <option value={8}>{isTimeframeModeActive ? '8 Periods' : '8 months'}</option>
              <option value={12}>{isTimeframeModeActive ? '12 Periods' : '12 months'}</option>
              <option value={18}>{isTimeframeModeActive ? '18 Periods' : '18 months'}</option>
              <option value={24}>{isTimeframeModeActive ? '24 Periods' : '24 months'}</option>
              <option value={30}>{isTimeframeModeActive ? '30 Periods' : '30 months'}</option>
              <option value={36}>{isTimeframeModeActive ? '36 Periods' : '36 months'}</option>
            </select>
            <select
              value={labelType}
              onChange={(e) => setLabelType(e.target.value as LabelType)}
              className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
            >
              <option value="date_range">Months</option>
              <option value="period">Time Period</option>
            </select>
            {user?.role === 'admin' && (
              <button className="btn btn-primary text-xs px-2 py-1 ml-auto" onClick={() => navigate('/admin/goals')}>
                + Goal
              </button>
            )}
          </div>
          {/* Row 2: Filters & grouping */}
          {data && data.metrics.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
              <span className="text-xs font-medium text-gray-500 uppercase">Filter:</span>
              <select
                value={ownerFilter}
                onChange={(e) => setOwnerFilter(e.target.value)}
                className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
              >
                <option value="">All Owners</option>
                {uniqueOwners.map(o => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
              <select
                value={areaFilter}
                onChange={(e) => setAreaFilter(e.target.value)}
                className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
              >
                <option value="">All Areas</option>
                {areas?.filter(a => a.is_active).map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              <select
                value={labelType === 'period' ? 'timeframe' : groupBy}
                onChange={(e) => setGroupBy(e.target.value as GroupBy)}
                disabled={labelType === 'period'}
                className={`px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-brand-teal focus:border-brand-teal ${
                  labelType === 'period' ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                title={labelType === 'period' ? 'Grouping is forced to Timeframe in period mode' : undefined}
              >
                <option value="none">No Grouping</option>
                <option value="owner">Group by Owner</option>
                <option value="timeframe">Group by Timeframe</option>
                <option value="area">Group by Area</option>
              </select>
              {(ownerFilter || areaFilter) && (
                <button
                  onClick={() => { setOwnerFilter(''); setAreaFilter('') }}
                  className="text-sm text-brand-teal hover:text-brand-teal/80"
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal" />
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
          <p className="text-danger-700">Failed to load scorecard: {error.message}</p>
        </div>
      )}

      {/* Data loaded - Regular mode */}
      {data && !isTimeframeModeActive && (
        <>
          {/* Personalized summary stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-success-100 rounded-lg border border-success-200 p-3">
              <div className="text-xl font-bold text-white">{mySummary.met}</div>
              <div className="text-xs text-white/90">My Goals Met</div>
            </div>
            <div className="bg-danger-100 rounded-lg border border-danger-200 p-3">
              <div className="text-xl font-bold text-white">{mySummary.missed}</div>
              <div className="text-xs text-white/90">My Goals Missed</div>
            </div>
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
              <div className="text-xl font-bold text-gray-700">{mySummary.pending}</div>
              <div className="text-xs text-gray-500">My Pending</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <div className="text-xl font-bold text-gray-900">{teamMetCount}</div>
              <div className="text-xs text-gray-500">Team Goals Met</div>
            </div>
          </div>

          {/* My Metrics Section */}
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">My Metrics</h2>
            {myMetrics.length > 0 ? (
              <ComprehensiveScorecardTable
                metrics={myMetrics}
                periods={data.periods}
                onRowClick={handleRowClick}
              />
            ) : (
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center">
                <p className="text-gray-500">No metrics assigned to you.</p>
              </div>
            )}
          </div>

          {/* Team Metrics Section */}
          {teamMetrics.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold text-gray-900">Team Metrics</h2>
              {renderGroupedMetrics(teamMetrics)}
            </div>
          )}
        </>
      )}

      {/* Data loaded - Time Period mode */}
      {isTimeframeModeActive && !isLoading && timeframeScorecards.dataByTimeframe.size > 0 && (
        <>
          {/* Personalized summary stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-success-100 rounded-lg border border-success-200 p-3">
              <div className="text-xl font-bold text-white">{timeframeSummary.myMet}</div>
              <div className="text-xs text-white/90">Goals Met</div>
            </div>
            <div className="bg-danger-100 rounded-lg border border-danger-200 p-3">
              <div className="text-xl font-bold text-white">{timeframeSummary.myMissed}</div>
              <div className="text-xs text-white/90">Goals Missed</div>
            </div>
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
              <div className="text-xl font-bold text-gray-700">{timeframeSummary.myPending}</div>
              <div className="text-xs text-gray-500">Pending</div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <div className="text-xl font-bold text-gray-900">{timeframeSummary.teamMet}</div>
              <div className="text-xs text-gray-500">Team Goals Met</div>
            </div>
          </div>

          {/* My Metrics Section - grouped by timeframe */}
          {(() => {
            const myTimeframeGroups = (['weekly', 'monthly', 'quarterly', 'biannual', 'yearly'] as Timeframe[])
              .map(tf => {
                const tfData = timeframeScorecards.dataByTimeframe.get(tf)
                if (!tfData) return null
                const filtered = tfData.metrics
                  .filter(m => m.owner_id === user?.id)
                  .filter(m => {
                    if (ownerFilter && m.owner_id !== ownerFilter) return false
                    if (areaFilter && m.area_id !== areaFilter) return false
                    return true
                  })
                if (filtered.length === 0) return null
                return { tf, metrics: filtered, periods: tfData.periods }
              })
              .filter(Boolean) as { tf: Timeframe; metrics: typeof data extends null ? never : NonNullable<typeof data>['metrics']; periods: { label: string; start: number; end: number }[] }[]

            if (myTimeframeGroups.length === 0) return null

            return (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-900">My Metrics</h2>
                {myTimeframeGroups.map(({ tf, metrics, periods }) => (
                  <div key={tf} className="space-y-2">
                    <h3 className="text-sm font-medium text-gray-600 pl-2">
                      {TIMEFRAME_LABELS[tf]}
                      <span className="text-xs text-gray-400 ml-2">({metrics.length})</span>
                    </h3>
                    <ComprehensiveScorecardTable
                      metrics={metrics}
                      periods={periods}
                      onRowClick={handleRowClick}
                      timeframe={tf}
                    />
                  </div>
                ))}
              </div>
            )
          })()}

          {/* Team Metrics Section - grouped by timeframe */}
          {(() => {
            const teamTimeframeGroups = (['weekly', 'monthly', 'quarterly', 'biannual', 'yearly'] as Timeframe[])
              .map(tf => {
                const tfData = timeframeScorecards.dataByTimeframe.get(tf)
                if (!tfData) return null
                const filtered = tfData.metrics
                  .filter(m => m.owner_id !== user?.id)
                  .filter(m => {
                    if (ownerFilter && m.owner_id !== ownerFilter) return false
                    if (areaFilter && m.area_id !== areaFilter) return false
                    return true
                  })
                if (filtered.length === 0) return null
                return { tf, metrics: filtered, periods: tfData.periods }
              })
              .filter(Boolean) as { tf: Timeframe; metrics: typeof data extends null ? never : NonNullable<typeof data>['metrics']; periods: { label: string; start: number; end: number }[] }[]

            if (teamTimeframeGroups.length === 0) return null

            return (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-900">Team Metrics</h2>
                {teamTimeframeGroups.map(({ tf, metrics, periods }) => (
                  <div key={tf} className="space-y-2">
                    <h3 className="text-sm font-medium text-gray-600 pl-2">
                      {TIMEFRAME_LABELS[tf]}
                      <span className="text-xs text-gray-400 ml-2">({metrics.length})</span>
                    </h3>
                    <ComprehensiveScorecardTable
                      metrics={metrics}
                      periods={periods}
                      onRowClick={handleRowClick}
                      timeframe={tf}
                    />
                  </div>
                ))}
              </div>
            )
          })()}
        </>
      )}

      {/* Empty state for when there's no metrics */}
      {data && data.metrics.length === 0 && !isTimeframeModeActive && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">No metrics yet</h3>
          <p className="text-gray-500 mb-4">Get started by creating your first metric.</p>
          {user?.role === 'admin' && (
            <button className="btn btn-primary" onClick={() => navigate('/admin/metrics')}>Create Metric</button>
          )}
        </div>
      )}

      {/* Empty state for Time Period mode */}
      {isTimeframeModeActive && !isLoading && timeframeScorecards.dataByTimeframe.size === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">No metrics yet</h3>
          <p className="text-gray-500 mb-4">Get started by creating your first metric.</p>
          {user?.role === 'admin' && (
            <button className="btn btn-primary" onClick={() => navigate('/admin/metrics')}>Create Metric</button>
          )}
        </div>
      )}
    </div>
  )
}
